import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingScreen from '../components/LoadingScreen';
import { useAuth, AUTH_REDIRECT_NEXT_KEY } from '../context/useAuth';
import { supabase, isSupabaseEnabled } from '../lib/supabase';
import { clearAuthSessionCache } from '../lib/authSession';
import { BUYER_MAP_PATH, isBuyerOnlyUser } from '../utils/accessControl';
import { resolveNextPath } from '../utils/authLanding';

const AUTH_STORAGE_KEY = 'shadowland-auth';

function readStoredNextPath() {
  try {
    const value = sessionStorage.getItem(AUTH_REDIRECT_NEXT_KEY);
    sessionStorage.removeItem(AUTH_REDIRECT_NEXT_KEY);
    return value;
  } catch {
    return '';
  }
}

function isMissingPkceVerifierError(err) {
  const message = String(err?.message || err?.error_description || err || '');
  return /PKCE code verifier.*not found|code verifier.*not found/i.test(message);
}

function isRetryableOAuthCodeExchangeError(err) {
  const message = String(err?.message || err?.error_description || err || '');
  return isMissingPkceVerifierError(err)
    || /Unable to exchange external code|invalid_grant|authorization code.*invalid|authorization code.*expired/i.test(message);
}

function isAccessDeniedError(err) {
  return err?.code === 'INVITE_REQUIRED' || /invite required|approved account/i.test(String(err?.message || ''));
}

function clearSupabaseAuthStorageForRetry() {
  clearAuthSessionCache();
  try { localStorage.removeItem(AUTH_STORAGE_KEY); } catch { /* ignore unavailable storage */ }
  try { sessionStorage.removeItem(AUTH_STORAGE_KEY); } catch { /* ignore unavailable storage */ }
}

export default function AuthCallback() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const ranRef = useRef(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const finish = async () => {
      const params = new URLSearchParams(window.location.search || '');
      const hashParams = new URLSearchParams(String(window.location.hash || '').replace(/^#/, ''));
      const callbackError = params.get('error_description')
        || params.get('error')
        || hashParams.get('error_description')
        || hashParams.get('error')
        || '';
      const code = params.get('code') || '';
      const next = resolveNextPath({
        paramNext: params.get('next') || '',
        storedNext: readStoredNextPath() || '',
      });

      if (!isSupabaseEnabled || !supabase) {
        navigate('/login?auth_error=Supabase%20is%20not%20configured', { replace: true });
        return;
      }

      if (callbackError) {
        const retryableOAuthExchange = isRetryableOAuthCodeExchangeError(callbackError);
        if (retryableOAuthExchange) clearSupabaseAuthStorageForRetry();
        const friendlyMessage = retryableOAuthExchange
          ? 'Your sign-in session expired. Please start Google sign-in again in this browser.'
          : callbackError;
        const message = encodeURIComponent(friendlyMessage);
        navigate(`/login?auth_error=${message}`, { replace: true });
        return;
      }

      try {
        clearSupabaseAuthStorageForRetry();
        if (code) {
          const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
          if (exchangeError) throw exchangeError;
          clearAuthSessionCache();
        }

        const { data: { session } = {}, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) throw sessionError;
        if (!session?.user) throw new Error('Google sign-in did not return a session. Please try again.');

        const refreshedUser = await refreshUser();
        if (!refreshedUser) {
          const missingProfile = new Error('Admin invite required');
          missingProfile.code = 'INVITE_REQUIRED';
          throw missingProfile;
        }
        navigate(isBuyerOnlyUser(refreshedUser) ? BUYER_MAP_PATH : next, { replace: true });
      } catch (err) {
        const retryableOAuthExchange = isRetryableOAuthCodeExchangeError(err);
        if (isAccessDeniedError(err)) {
          console.warn('[AuthCallback] denied uninvited OAuth login:', err?.message);
          clearSupabaseAuthStorageForRetry();
          try { await supabase.auth.signOut(); } catch { /* ignore signout failure */ }
          navigate('/login?auth_error=Admin%20invite%20required', { replace: true });
          return;
        }
        if (!retryableOAuthExchange) {
          console.error('[AuthCallback] OAuth callback failed:', err);
        }
        clearSupabaseAuthStorageForRetry();
        const friendlyMessage = retryableOAuthExchange
          ? 'Your sign-in session expired. Please start Google sign-in again in this browser.'
          : (err?.message || 'Google sign-in failed. Please try again.');
        const message = encodeURIComponent(friendlyMessage);
        if (retryableOAuthExchange) {
          navigate(`/login?auth_error=${message}`, { replace: true });
          return;
        }
        setError(friendlyMessage);
        window.setTimeout(() => {
          navigate(`/login?auth_error=${message}`, { replace: true });
        }, 900);
      }
    };

    void finish();
  }, [navigate, refreshUser]);

  if (error) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-error">{error}</div>
        </div>
      </div>
    );
  }

  return <LoadingScreen />;
}
